import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export type PortalUiLang = 'ru' | 'en' | 'id'

const STORAGE_KEY = 'portal_ui_lang'
const COOKIE_NAME = 'lang'
const COOKIE_MAX_AGE = 31536000

function readCookieLang(): PortalUiLang | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
  const v = (m?.[1] || '').trim().toLowerCase()
  if (v === 'ru' || v === 'en' || v === 'id') return v
  return null
}

export function hasStoredPortalUiLanguage(): boolean {
  try {
    const stored = (localStorage.getItem(STORAGE_KEY) || '').trim().toLowerCase()
    if (stored === 'ru' || stored === 'en' || stored === 'id') return true
  } catch {
    /* private mode */
  }
  return readCookieLang() !== null
}

function readStoredLang(): PortalUiLang {
  try {
    const s = (localStorage.getItem(STORAGE_KEY) || '').trim().toLowerCase()
    if (s === 'ru' || s === 'en' || s === 'id') return s
  } catch {
    /* private mode */
  }
  return readCookieLang() || 'ru'
}

function writeLangCookie(lang: PortalUiLang) {
  try {
    document.cookie = `${COOKIE_NAME}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
    document.cookie = `portal_lang=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
  } catch {
    /* */
  }
}

type Ctx = {
  lang: PortalUiLang
  setLang: (lang: PortalUiLang) => void
  bindAccountLanguage: (account: string, profileLanguage?: PortalUiLang) => void
}

const PortalUiLanguageContext = createContext<Ctx | null>(null)

export function PortalUiLanguageProvider({ children }: { children: ReactNode }) {
  const languageAccount = useRef('')
  const [lang, setLangState] = useState<PortalUiLang>(() => readStoredLang())

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: PortalUiLang) => {
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
      if (languageAccount.current) localStorage.setItem(`${STORAGE_KEY}:${languageAccount.current}`, next)
    } catch {
      /* */
    }
    writeLangCookie(next)
  }, [])

  const bindAccountLanguage = useCallback((account: string, profileLanguage?: PortalUiLang) => {
    if (languageAccount.current === account) return
    languageAccount.current = account
    if (!account) return
    let saved = ''
    try { saved = localStorage.getItem(`${STORAGE_KEY}:${account}`) || '' } catch { /* private mode */ }
    // A language left by another login must never override this employee's profile.
    const next = saved === 'ru' || saved === 'en' || saved === 'id' ? saved : profileLanguage || 'en'
    setLang(next)
  }, [setLang])

  const value = useMemo(() => ({ lang, setLang, bindAccountLanguage }), [lang, setLang, bindAccountLanguage])

  return <PortalUiLanguageContext.Provider value={value}>{children}</PortalUiLanguageContext.Provider>
}

export function usePortalUiLanguage(): Ctx {
  const v = useContext(PortalUiLanguageContext)
  if (!v) {
    throw new Error('usePortalUiLanguage must be used within PortalUiLanguageProvider')
  }
  return v
}
